import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

// Set up axios interceptor to always send token
axios.interceptors.request.use((config) => {
  const token = localStorage.getItem('access_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  config.withCredentials = true;
  return config;
});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = async () => {
    const token = localStorage.getItem('access_token');
    if (!token) {
      setUser(false);
      setLoading(false);
      return;
    }
    try {
      const { data } = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/auth/me`
      );
      setUser(data);
    } catch (error) {
      localStorage.removeItem('access_token');
      setUser(false);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    checkAuth();
  }, []);

  const saveToken = (token) => {
    if (token) {
      localStorage.setItem('access_token', token);
    }
  };

  const login = async (username, password) => {
    const { data } = await axios.post(
      `${process.env.REACT_APP_BACKEND_URL}/api/auth/login`,
      { username, password }
    );
    if (data.access_token) {
      saveToken(data.access_token);
    }
    setUser(data);
    return data;
  };

  const register = async (username, password, email) => {
    const { data } = await axios.post(
      `${process.env.REACT_APP_BACKEND_URL}/api/auth/register`,
      { username, password, email }
    );
    if (data.access_token) {
      saveToken(data.access_token);
    }
    setUser(data);
    return data;
  };

  const logout = async () => {
    await axios.post(
      `${process.env.REACT_APP_BACKEND_URL}/api/auth/logout`,
      {}
    );
    localStorage.removeItem('access_token');
    setUser(false);
  };

  const updateUser = (userData) => {
    if (userData && userData.access_token) {
      saveToken(userData.access_token);
    }
    setUser(userData);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, updateUser, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
};
